import {
    Avatar,
    Button,
    Modal,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalHeader,
    Select,
    SelectItem,
    Textarea,
} from '@heroui/react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { updateUserStatus } from '../../services/api';
import { EditStatusModalProps } from '../../types/types';

type ApiError = {
    response?: {
        data?: {
            message?: unknown;
        };
    };
};

const getApiErrorMessage = (error: unknown): string => {
    const message = (error as ApiError).response?.data?.message;

    return typeof message === 'string' && message.trim()
        ? message
        : 'Failed to update profile status';
};

const EditProfileStatus: React.FC<EditStatusModalProps> = ({
    isOpen,
    onClose,
    profile,
}) => {
    const [selectedStatus, setSelectedStatus] = useState<string>(profile.profileStatus);
    const [reason, setReason] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const { t } = useTranslation('editProfileStatus');

    const getInitials = (firstName: string, lastName: string) => {
        return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
    };

    const requiresReason = selectedStatus === 'ARCHIVED' || selectedStatus === 'SUSPENDED';

    const handleSave = async () => {
        if (!selectedStatus || selectedStatus === profile.profileStatus) {
            return;
        }
        if (requiresReason && !reason.trim()) {
            toast.error(t('reasonRequired'));
            return;
        }

        setIsLoading(true);

        try {
            const updateUserStatusPayload = {
                profileId: profile.id,
                profileStatus: selectedStatus,
                reason: requiresReason ? reason.trim() : undefined
            };

            await updateUserStatus(updateUserStatusPayload);
            toast.success(t('status_toast_message'));
            onClose();
        } catch (err: unknown) {
            toast.error(getApiErrorMessage(err));
        } finally {
            setIsLoading(false);
        }
    };

    const statusOptions = [
        { key: 'ACTIVE', label: 'Active', selectable: true },
        { key: 'INACTIVE', label: 'Inactive', selectable: true },
        { key: 'ARCHIVED', label: 'Archived', selectable: true },
        { key: 'SUSPENDED', label: 'Suspended', selectable: true },
        { key: 'PENDING', label: 'Pending', selectable: false },
    ];

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            size="md"
            placement="center"
            backdrop="opaque"
        >
            <ModalContent>
                <ModalHeader className="flex flex-col gap-1">
                    <span className="text-lg font-semibold">{t('title')}</span>
                </ModalHeader>

                <ModalBody>
                    <div className="space-y-4">
                        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                            <Avatar
                                src={profile.profileImage}
                                alt={`${profile.firstName} ${profile.lastName}`}
                                name={getInitials(profile.firstName, profile.lastName)}
                                size="md"
                            />
                            <div>
                                <p className="font-semibold text-gray-900">
                                    {profile.firstName} {profile.lastName}
                                </p>
                                <p className="text-sm text-gray-600">{profile.username}</p>
                                <p className="text-xs text-gray-500">
                                    {t('currentStatusPrefix')} {profile.profileStatus}
                                </p>
                            </div>
                        </div>

                        <div>
                            <p className="text-sm font-medium text-gray-700 mb-2">
                                {t('selectStatusLabel')} <span className="text-red-500">*</span>
                            </p>
                            <Select
                                placeholder={t('selectStatusLabel')}
                                selectedKeys={selectedStatus ? [selectedStatus] : []}
                                onSelectionChange={(keys) =>
                                    setSelectedStatus(Array.from(keys)[0] as string)
                                }
                                className="w-full"
                                isDisabled={isLoading}
                            >
                                {statusOptions.map((option) => (
                                    <SelectItem
                                        key={option.key}
                                        isDisabled={!option.selectable}
                                    >
                                        {option.label}
                                    </SelectItem>
                                ))}
                            </Select>
                        </div>

                        {requiresReason && (
                            <div>
                                <p className="text-sm font-medium text-gray-700 mb-2">
                                    {t('reasonLabel')} <span className="text-red-500">*</span>
                                </p>
                                <Textarea
                                    placeholder={t('reasonPlaceholder')}
                                    value={reason}
                                    onChange={(e) => setReason(e.target.value)}
                                    className="w-full"
                                    minRows={3}
                                    maxRows={5}
                                    maxLength={200}
                                    isDisabled={isLoading}
                                />
                                <div className="flex justify-between items-center mt-1">
                                    <span className="text-xs text-gray-500">
                                        {requiresReason ? t('reasonRequired') : ''}
                                    </span>
                                    <span className="text-xs text-gray-500">
                                        {reason.length}/200 {t('characters')}
                                    </span>
                                </div>
                            </div>
                        )}

                    </div>
                </ModalBody>

                <ModalFooter>
                    <Button
                        variant="light"
                        onPress={onClose}
                        isDisabled={isLoading}
                    >
                        {t('cancelButton')}
                    </Button>
                    <Button
                        color="primary"
                        onPress={handleSave}
                        isDisabled={
                            !selectedStatus ||
                            selectedStatus === profile.profileStatus ||
                            isLoading ||
                            (requiresReason && !reason.trim())
                        }
                        isLoading={isLoading}
                    >
                        {isLoading ? t('savingButton') : t('saveButton')}
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default EditProfileStatus;
